import * as config from 'serviceWorker/db/config';
import { swSignals } from 'serviceWorker/events/swSignals';
import { getCurrentUserIdentity, rememberCurrentUser } from 'serviceWorker/helpers/currentUserIdentity';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { CurrentUserIdentity, isKnownValue } from 'shared/domain/currentUser/currentUserIdentity';
import { LogLevel } from 'shared/types/logger';

/**
 * The logged-in user, as the page announced it or the permissions download
 * returned it: the worker logs as them, also after a restart (PT-7276). It is
 * stored once the config is (a first login may announce the user before);
 * the same user is not written again.
 */
export function identifyCurrentUser(identity: CurrentUserIdentity): void {
  rememberCurrentUser(identity);
  if (!isKnownValue(identity.id)) {
    return;
  }
  config.setCurrentUser(identity).catch((error: unknown) => {
    swLog('Could not store the current user', LogLevel.INFO, error, null);
  });
}

/**
 * The user the worker logged as before it was stopped, until the page
 * announces one: the repair at start logs before the page can (PT-7276).
 */
export async function restoreCurrentUserIdentity(): Promise<void> {
  try {
    const stored = (await config.get())?.currentUser;
    if (stored && !isKnownValue(getCurrentUserIdentity().id)) {
      rememberCurrentUser(stored);
    }
  } catch (error: unknown) {
    swLog('Could not read the stored current user', LogLevel.INFO, error, null);
  }
}

/**
 * A logout forgets the stored user: a later start does not log as them. The
 * one in memory stays for the logs of the logout itself.
 */
export function init(): void {
  swSignals.loggedOut.subscribe(() => {
    config.clearCurrentUser().catch((error: unknown) => {
      swLog('Could not forget the stored current user', LogLevel.INFO, error, null);
    });
  });
}
